/**
 * Chat history service for AskAsper
 * Fetches conversation sessions and thread messages.
 */

import type { RightPanelFeedbackPayload } from 'asper_design_library';
import type { AgenticFeedbackHistoryResponse } from 'asper_design_library';
import { ASK_ASPER_BASE_URL } from '../config';

/** Payload for PUT …/update-feedback (multipart/form-data; Agentic right-panel thumbs / text / files). */
export interface AgenticFeedbackUpdateRequest {
    feedback_level: 'query' | 'task';
    run_id: string;
    task_id?: string;
    session_id?: string;
    thumbs_up: 'like' | 'dislike' | 'none';
    /** Empty string allowed for files-only submits. */
    subjective_answer?: string | null;
    attachments?: File[];
}

/**
 * Maps messages-mode right-panel feedback context to PUT /update-feedback body.
 * Returns null if `run_id` is missing on the payload.
 */
export function buildFeedbackUpdateFromRightPanelPayload(
    payload: RightPanelFeedbackPayload,
    thumbs_up: AgenticFeedbackUpdateRequest['thumbs_up'],
    subjective_answer?: string | null,
    attachments?: File[],
    session_id?: string,
): AgenticFeedbackUpdateRequest | null {
    const run_id = payload.run_id;
    if (!run_id) return null;

    const files = attachments?.length ? attachments : undefined;

    if (payload.kind === 'summary') {
        return {
            feedback_level: 'query',
            run_id,
            session_id,
            thumbs_up,
            subjective_answer,
            attachments: files,
        };
    }

    return {
        feedback_level: 'task',
        run_id,
        task_id: payload.taskId,
        session_id,
        thumbs_up,
        subjective_answer,
        attachments: files,
    };
}

/** Thread shape expected by AgenticTwoPanelLayout sidebar */
export interface SidebarThread {
    id: number;
    thread_id: string;
    thread_name: string;
    user_id: string;
    created_on: string;
    is_pinned: boolean | null;
    pin_no: number | null;
}

/** Raw chat session from GET /chat-sessions API */
export interface ChatSessionApiResponse {
    id?: number | string;
    thread_id?: string;
    thread_name?: string;
    user_id?: string;
    created_on?: string;
    created_at?: string;
    is_pinned?: boolean | null;
    pin_no?: number | null;
    [key: string]: unknown;
}

/** Raw message from chat history API */
export interface ChatHistoryMessageApi {
    id?: number | string;
    thread_id?: string;
    run_id?: string;
    type?: string;
    content?: string;
    user_input?: string;
    question?: string;
    created_on?: string;
    thumbs_up?: boolean | null;
    subjective_answer?: string | null;
    tool_calls?: unknown[];
    additional_kwargs?: {
        intermediate_steps?: Array<{
            step: string;
            status: string;
            content: string | unknown;
            timestamp: number;
        }>;
        charts?: Array<{ viz_data: unknown; html?: string; engine?: string; assets?: unknown; render?: unknown }>;
        followup_questions?: string;
    };
    answer?: {
        data?: {
            output?: string;
            intermediate_steps?: unknown[];
            visualizations?: Array<{
                viz_data: unknown;
                task_id?: string;
                task_name?: string;
                html?: string;
                engine?: string;
                assets?: unknown;
                render?: unknown;
            }>;
            followup_question?: unknown;
        };
    };
    charts?: Array<{
        viz_data: unknown;
        task_id?: string;
        task_name?: string;
        html?: string;
        engine?: string;
        assets?: unknown;
        render?: unknown;
    }>;
}

function getAuthHeader(): string {
    const token = localStorage.getItem('accessToken');
    return `Bearer ${token}`;
}

/**
 * Fetch chat sessions (conversation threads) from the API.
 */
export async function fetchChatSessions(): Promise<ChatSessionApiResponse[]> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}chat-sessions`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        const extracted = Array.isArray(data) ? data : (data?.sessions ?? data?.data ?? data?.chat_sessions ?? []);
        return Array.isArray(extracted) ? extracted : [];
    }

    return [];
}

/**
 * Map API chat sessions to thread format expected by AgenticTwoPanelLayout sidebar.
 */
export function mapChatSessionsToThreads(sessions: ChatSessionApiResponse[] | null): SidebarThread[] {
    if (!sessions || !Array.isArray(sessions)) {
        return [];
    }

    return sessions.map((s, index) => ({
        id: typeof s.id === 'number' ? s.id : index,
        thread_id: s.thread_id ?? '',
        thread_name: s.thread_name ?? `Thread ${index + 1}`,
        user_id: s.user_id ?? '',
        created_on: s.created_on ?? (s as { created_at?: string }).created_at ?? '',
        is_pinned: s.is_pinned ?? null,
        pin_no: s.pin_no ?? null,
    }));
}


export interface RenameThreadRequest {
    thread_id: string;
    new_thread_name: string;
}

/**
 * Rename a thread
 */
export async function renameThread(request: RenameThreadRequest): Promise<void> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}chat-session/rename`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
}

/**
 * Delete a thread and its messages
 */
export async function deleteThread(threadId: string): Promise<void> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}chat-session/delete/${threadId}`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
}


/**
 * Fetch chat history (messages) for a specific thread.
 * Endpoint: GET /chat-sessions/{threadId}/messages
 */
export async function getChatHistory(threadId: string): Promise<ChatHistoryMessageApi[]> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}chat-history/${threadId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return Array.isArray(data) ? data : (data?.messages ?? data?.data ?? []);
    }

    return [];
}

/**
 * Load per-run feedback for the Agentic right panel (GET get-feedback-history/{thread_id}).
 */
export async function getFeedbackHistory(threadId: string): Promise<AgenticFeedbackHistoryResponse> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}get-feedback-history/${encodeURIComponent(threadId)}`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }
    return response.json() as Promise<AgenticFeedbackHistoryResponse>;
}

/**
 * Persist Agentic right-panel feedback (query- or task-level) as multipart/form-data.
 * Do not set Content-Type — the browser sets the multipart boundary.
 */
export async function updateAgenticFeedback(payload: AgenticFeedbackUpdateRequest): Promise<void> {
    const form = new FormData();
    form.append('thumbs_up', payload.thumbs_up);
    form.append('subjective_answer', payload.subjective_answer ?? '');
    form.append('feedback_level', payload.feedback_level);
    form.append('run_id', payload.run_id);
    if (payload.task_id) form.append('task_id', payload.task_id);
    if (payload.session_id) form.append('session_id', payload.session_id);
    for (const file of payload.attachments ?? []) {
        form.append('attachments', file);
    }

    const response = await fetch(`${ASK_ASPER_BASE_URL}update-feedback`, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
        body: form,
    });

    if (!response.ok) {
        let detail: string | undefined;
        try {
            const body = await response.json();
            if (typeof body?.detail === 'string') detail = body.detail;
        } catch {
            // ignore non-JSON error body
        }
        const err = new Error(`Error: ${response.status} ${response.statusText}`) as Error & { detail?: string };
        if (detail) err.detail = detail;
        throw err;
    }
}
